import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ChangeEvent } from 'react';
import { MarketBaseSettingsPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketBaseSettings/MarketBaseSettings';

export class MarketBaseSettingsState {
    @observable inPlayDelay: string | null = null;
    @observable inPlayError: string | null = null;
    @observable eachWayStakeReduction: string | null = null;
    @observable eachWayStakeReductionError: string | null = null;

    constructor(
        readonly appState: AppState,
        readonly props: MarketBaseSettingsPropsType
    ) {
        makeAutoObservable(this);
    }

    @computed get getInPlayDelay(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        return this.inPlayDelay !== null ? this.inPlayDelay : marketModel.inPlayDelay.toString();
    }

    @action onChangeInPlayDelay = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        if (isNaN(Number(value)) === true) {
            this.inPlayError = 'Only digits are allowed.';
        } else {
            this.inPlayError = null;
        }
        this.inPlayDelay = event.target.value;
    };

    @action onSaveInPlayDelay = (): void => {
        if (this.inPlayError !== null) {
            return;
        }
        const delayPayload =
            this.inPlayDelay === null ? this.getInPlayDelay : this.inPlayDelay.trim() === '' ? '0' : this.inPlayDelay;
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            inPlayDelay: parseInt(delayPayload, 10),
        });
    };

    @computed get getEachWayStakeReduction(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        return this.eachWayStakeReduction !== null
            ? this.eachWayStakeReduction
            : marketModel.eachWayStakeReduction.toString();
    }

    @action onChangeEachWayStake = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        if (isNaN(Number(value)) === true) {
            this.eachWayStakeReductionError = 'Only digits are allowed.';
        } else {
            this.eachWayStakeReductionError = null;
        }

        this.eachWayStakeReduction = event.target.value;
    };

    @action onSaveEachWayStake = (): void => {
        if (this.eachWayStakeReductionError !== null) {
            return;
        }
        const eachWayPayload =
            this.eachWayStakeReduction === null || this.eachWayStakeReduction?.trim() === ''
                ? '0'
                : this.eachWayStakeReduction;
        this.appState.eventStateActions.saveMarketChanges(
            this.props.marketId,
            { eachWayStakeReduction: eachWayPayload },
            true
        );
    };

    @action onChangeStartingPrice = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { sp: event.target.checked });
    };

    @action onChangeStartingPriceOnly = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { spOnly: event.target.checked });
    };

    @action onChangeBestOdds = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { bp: event.target.checked });
    };

    @action onChangeBlockSettlement = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            blockSettlement: event.target.checked,
        });
    };

    @action onChangeBetReferral = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(
            this.props.marketId,
            { betReferralEnabled: event.target.checked },
            true
        );
    };

    @action onChangeSinglesOnly = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { singlesOnly: event.target.checked });
    };

    @action onChangeTraderInPlay = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { tradedInPlay: event.target.checked });
    };
}
